import { identity } from 'ramda'

import {
  take,
  put,
  fork,
  cancel,
  cancelled,
  all,
  select,
  call,
  takeLatest,
} from 'redux-saga/effects'
import { raceIf, createMockTask } from 'utils/sagas'
import { insertIf } from 'utils/types'

import strings from 'utils/strings'
import { NOTIFICATION_WARNING } from 'utils/constants'
import { VALIDATION_NOTIFY } from 'utils/notifications'
import { getErrorParentIds } from 'schemas/validation'

import {
  createSystemNotification,
  deleteSystemNotification,
} from 'actions/application'

import {
  setErrors,
  setError,
  removeError,
  SET_ERRORS,
  REMOVE_ERROR,
  RESET_ERRORS,
  resetErrors,
  STOP_WATCHING_ERRORS,
  stopWatchingErrors,
} from 'actions/validation'

import { getInErrorState } from 'selectors/validation'

// initialize all the handlers for validation watching
function* handleChanges(message, handlers) {
  try {
    while (true) {
      if (message) {
        yield put(
          createSystemNotification({
            notificationMessage: message,
            notificationType: NOTIFICATION_WARNING,
            notificationId: VALIDATION_NOTIFY,
            persistent: true,
            actionTrigger: {
              onClick: resetErrors,
              text: strings.dismiss,
              needsDispatch: true,
              type: 'secondary',
            },
          })
        )
      }

      yield all(handlers.map(call))
    }
  } finally {
    if (yield cancelled() && message) {
      yield put(deleteSystemNotification({ notificationId: VALIDATION_NOTIFY }))
    }
  }
}

// watch for removed errors
function* watchErrors() {
  while (true) {
    yield take([REMOVE_ERROR, RESET_ERRORS])

    const inErrorState = yield select(getInErrorState)
    if (!inErrorState) {
      yield put(stopWatchingErrors())
    }
  }
}

export function* watchErrorsTask({
  actionTypes,
  message,
  handlers,
  perpetual,
}) {
  // watch for errors to be made, or a cancel action to be fired (typically a success type)
  const [cancelAction] = yield raceIf(!perpetual, [
    take(actionTypes.cancel || 'UNUSED_ACTION'),
    take(SET_ERRORS),
  ])

  if (cancelAction) yield cancel()

  // start watching for errors and changes
  const [handleChangesTask, watchRemoveErrorTask] = yield all([
    fork(handleChanges, message, handlers),
    ...insertIf(!perpetual, fork(watchErrors)),
    ...insertIf(perpetual, createMockTask()),
  ])

  yield take([actionTypes.abort, STOP_WATCHING_ERRORS])

  yield cancel([handleChangesTask, watchRemoveErrorTask])
  yield put(stopWatchingErrors())
}

// kicks off the other sagas
// takeLatest will cancel any previous running validations
// perpetual ensures that validation is constantly run (not reset when all errors are cleared)
export function* startErrorWatching({
  actionTypes,
  message,
  handlers,
  perpetual,
}) {
  yield takeLatest(actionTypes.activate, watchErrorsTask, {
    actionTypes,
    message,
    handlers,
    perpetual,
  })
}

// Validates and dispatches based on validation
// if 'id' is null then we treat it like the first validation (at top level)
export function* validate(schema, arg, id, context = {}, callback = identity) {
  try {
    yield schema.validate(arg, {
      abortEarly: false,
      strict: true,
      context,
    })

    if (id) yield put(removeError(id))
    else {
      yield put(resetErrors())
    }

    yield callback()
  } catch (err) {
    console.error(err)

    if (id) yield put(setError(id))
    else {
      const ids = getErrorParentIds(err)
      yield put(setErrors(ids))
    }
  }
}
